import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AddIcon from '@mui/icons-material/Add'
import BoltIcon from '@mui/icons-material/Bolt'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import SearchIcon from '@mui/icons-material/Search'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import React, { useMemo, useRef, useState } from 'react'
import {
  useGetIdentity,
  useGetList,
  useLocale,
  useNotify,
  useRedirect,
  useStore,
  useTranslate,
} from 'react-admin'
import UnassignedPatientBanner from '../clinic-team/UnassignedPatientBanner'
import { applyDLSBGMCorrection, formatGlucoseForDisplay } from '../common'
import { DataTruncationNotice, type TruncatableList } from '../components/DataTruncationNotice'
import PageHeader from '../components/PageHeader'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { useDeviceLastReadings } from '../hooks/useDeviceLastReadings'
import { useIsCompact } from '../hooks/useResponsive'
import HiddenPetsPanel from '../pets/HiddenPetsPanel'
import { STATUS_TONES, type StatusTone, TONE_ACCENT } from '../themes/statusColors'
import type { Activity, Device, Emr, Metric, Pet, User } from '../types'
import { GLUCOSE_THRESHOLDS } from '../types/dashboard'
import { formatActivityDescription } from '../utils/activityUtils'
import { authHeaders } from '../utils/api'
import { intlLocale } from '../utils/localeDate'

type OwnerLike = number | (User & { clinic_name?: string | null }) | null | undefined

type AlertStatus =
  | 'Stable'
  | 'Needs Review'
  | 'Hypoglycemia Alert'
  | 'Hyperglycemia Alert'
  | 'Stale Reading'
  | 'Data Gap'
  | 'Sensor Issue'
  | 'No Active Device'
  | 'Pending Client Confirmation'
  | 'Follow-up Due'

type CgmStatus = 'Active' | 'Inactive' | 'Pending' | 'No Device'

type FilterKey =
  | 'all'
  | 'active-cgm'
  | 'needs-review'
  | 'recent-alerts'
  | 'hypoglycemia'
  | 'hyperglycemia'
  | 'pending-confirmation'
  | 'follow-up'
  | 'my-patients'

type ClinicalPatient = Pet & {
  owner: OwnerLike
  ownerId?: number
  ownerName: string
  ownerEmail: string
  clinicName: string
  deviceId?: number | string
  deviceName?: string
  cgmStatus: CgmStatus
  alertStatus: AlertStatus
  pendingConfirmation: boolean
  followUpDate?: string | null
  lastReadingMmol?: number
  lastUpdatedAt?: Date
  priority: 'High' | 'Medium' | 'Low'
  searchText: string
}

type ActivityItem = {
  id: string
  color: string
  description: string
  patientName: string
  timeLabel: string
  petId?: number
}

// Priority-driven tones (see src/themes/statusColors.ts + docs/UI-STYLE-GUIDE.md):
// only real clinical states get saturated color; data/workflow states are neutral
// gray so an actual alert is always the loudest thing in a row.
const statusTone: Record<AlertStatus, StatusTone> = {
  'Hypoglycemia Alert': 'critical',
  'Hyperglycemia Alert': 'alert',
  'Needs Review': 'attention',
  Stable: 'positive',
  'Stale Reading': 'neutral',
  'Data Gap': 'neutral',
  'Sensor Issue': 'neutral',
  'No Active Device': 'neutral',
  'Pending Client Confirmation': 'neutral',
  'Follow-up Due': 'neutral',
}

// ── Patient list sorting ────────────────────────────────────────────────────
// Columns are click-to-sort. The default is the clinical triage order keyed on
// the Priority column: pets with live data first (no-data / no-device sink to
// the bottom), then High → Medium → Low priority, then most-recently-updated.
export type SortField =
  | 'priority'
  | 'patient'
  | 'species_breed'
  | 'cgm_status'
  | 'last_reading'
  | 'alert_status'
  | 'last_updated'

export type SortState = { field: SortField; order: 'asc' | 'desc' }

export const DEFAULT_SORT: SortState = { field: 'priority', order: 'asc' }

// Sensible starting direction when a vet first clicks a column header.
const DEFAULT_ORDER: Record<SortField, 'asc' | 'desc'> = {
  priority: 'asc', // High first
  patient: 'asc', // A → Z
  species_breed: 'asc',
  cgm_status: 'asc',
  last_reading: 'desc', // highest glucose first
  alert_status: 'asc', // most severe first
  last_updated: 'desc', // newest first
}

const priorityRank: Record<ClinicalPatient['priority'], number> = {
  High: 0,
  Medium: 1,
  Low: 2,
}

// Most-severe → least. Drives the Alert Status column sort.
const alertStatusRank: Record<AlertStatus, number> = {
  'Hypoglycemia Alert': 0,
  'Hyperglycemia Alert': 1,
  'Needs Review': 2,
  'Data Gap': 3,
  'Sensor Issue': 4,
  'Stale Reading': 5,
  'Follow-up Due': 6,
  'Pending Client Confirmation': 7,
  'No Active Device': 8,
  Stable: 9,
}

const hasReading = (p: ClinicalPatient) => p.lastUpdatedAt != null

const byRecencyDesc = (a: ClinicalPatient, b: ClinicalPatient) =>
  (b.lastUpdatedAt?.getTime() ?? 0) - (a.lastUpdatedAt?.getTime() ?? 0)

const byNameAsc = (a: ClinicalPatient, b: ClinicalPatient) =>
  (a.name ?? '').localeCompare(b.name ?? '')

const speciesBreed = (p: ClinicalPatient) =>
  `${p.species ?? ''} ${p.breed ?? ''}`.trim().toLowerCase()

// Undefined/null values always sort last, regardless of asc/desc direction.
const numNullsLast = (a: number | undefined, b: number | undefined, dir: number) => {
  const aNull = a == null
  const bNull = b == null
  if (aNull !== bNull) return aNull ? 1 : -1
  if (aNull && bNull) return 0
  return ((a as number) - (b as number)) * dir
}

export const comparePatients = (
  a: ClinicalPatient,
  b: ClinicalPatient,
  sort: SortState,
): number => {
  const dir = sort.order === 'asc' ? 1 : -1
  switch (sort.field) {
    case 'priority': {
      // No-data / no-device pets sink to the bottom in either direction, so the
      // triage view always surfaces actionable, data-bearing patients first.
      const aNo = !hasReading(a)
      const bNo = !hasReading(b)
      if (aNo !== bNo) return aNo ? 1 : -1
      if (priorityRank[a.priority] !== priorityRank[b.priority]) {
        return (priorityRank[a.priority] - priorityRank[b.priority]) * dir
      }
      return byRecencyDesc(a, b) || byNameAsc(a, b)
    }
    case 'last_reading':
      return numNullsLast(a.lastReadingMmol, b.lastReadingMmol, dir) || byNameAsc(a, b)
    case 'last_updated':
      return (
        numNullsLast(a.lastUpdatedAt?.getTime(), b.lastUpdatedAt?.getTime(), dir) || byNameAsc(a, b)
      )
    case 'cgm_status':
      return (a.cgmStatus ?? '').localeCompare(b.cgmStatus ?? '') * dir || byNameAsc(a, b)
    case 'alert_status':
      return (
        (alertStatusRank[a.alertStatus] - alertStatusRank[b.alertStatus]) * dir || byNameAsc(a, b)
      )
    case 'species_breed':
      return speciesBreed(a).localeCompare(speciesBreed(b)) * dir || byNameAsc(a, b)
    default:
      return byNameAsc(a, b) * dir
  }
}

// The chip text comes from pos.dashboard.clinical.filters.<key> (translated at render).
const filterOptions: Array<{ key: FilterKey }> = [
  { key: 'all' },
  { key: 'active-cgm' },
  { key: 'needs-review' },
  { key: 'recent-alerts' },
  { key: 'hypoglycemia' },
  { key: 'hyperglycemia' },
  { key: 'pending-confirmation' },
  // 'follow-up' filter hidden until vet follow-up scheduling exists (followUpDate
  // is never set today, so it would always be empty).
  { key: 'my-patients' },
]

const getOwner = (owner: OwnerLike) => (typeof owner === 'object' && owner ? owner : null)

const getPetId = (pet: number | Pet | null | undefined) =>
  typeof pet === 'object' && pet ? pet.id : pet

export const formatDate = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date)

type Translate = (key: string, options?: Record<string, unknown>) => string

// Rendered per locale: the label is NOT stored on the patient, because this used to bake
// English ("5 min ago") into the data and a Chinese-locale vet read it that way.
export const relativeTime = (
  date: Date | string | number | null | undefined,
  translate: Translate,
) => {
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`pos.dashboard.clinical.time.${key}`, options)
  if (!date) return translate('pos.dashboard.clinical.no_data')
  const value = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(value.getTime())) return translate('pos.dashboard.clinical.no_data')
  const diffMs = Date.now() - value.getTime()
  const abs = Math.max(0, diffMs)
  const minutes = Math.floor(abs / 60000)
  if (minutes < 1) return t('just_now')
  if (minutes < 60) return t('minutes_ago', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('hours_ago', { count: hours })
  const days = Math.floor(hours / 24)
  if (days === 1) return t('yesterday')
  return t('days_ago', { count: days })
}

const isToday = (date?: string | null) => {
  if (!date) return false
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return false
  const now = new Date()
  return parsed.toDateString() === now.toDateString()
}

const getFollowUpDate = (pet: Pet, emrs: Emr[]) => {
  const petEmrs = emrs.filter((emr) => emr.pet === pet.id)
  for (const emr of petEmrs) {
    const info = emr.additionalInfo
    if (info && typeof info === 'object' && !Array.isArray(info)) {
      const candidate =
        (info as Record<string, unknown>).followUpDate ||
        (info as Record<string, unknown>).follow_up_date ||
        (info as Record<string, unknown>).followUpAt
      if (typeof candidate === 'string') return candidate
    }
  }
  return null
}

const getLatestMetricForDevices = (metrics: Metric[], devices: Device[]) => {
  const deviceIds = new Set(devices.map((device) => device.id))
  return metrics.find((metric) => {
    const metricDeviceId = typeof metric.device === 'object' ? metric.device.id : metric.device
    return deviceIds.has(metricDeviceId)
  })
}

// Exported for unit testing: this is the seconds→ms boundary (S3) that feeds
// `lastUpdatedAt` into getAlertStatus's freshness gate. The API sends
// `metric.timestamp` in Unix SECONDS; dropping the `* 1000` makes every reading
// look ~56 years old → always 'Data Gap' → all hypo/hyper/stale badges silently
// disappear. See ClinicalDashboard.alertStatus.test.ts.
export const getMetricDate = (metric?: Metric) => {
  if (!metric) return undefined
  if (typeof metric.timestamp === 'number' && metric.timestamp > 0) {
    return new Date(metric.timestamp * 1000)
  }
  return metric.updatedAt ? new Date(metric.updatedAt) : undefined
}

// Reading must be at least this fresh to count as an active hyper/hypo
// alert. Aligned with the alert-engine's sustained-window check
// (config.DefaultSustainedWindowMinutes = 20 min); we add a small grace
// margin for clock skew + the engine's own processing tick (2 min). A
// reading older than this is what the engine itself would no longer
// classify — surfacing a red badge for it is a false positive
// (2026-06-15: pet 285379 dashboard kept showing "Hyperglycemia Alert"
// for hours after the sensor stopped streaming).
export const STALE_READING_MS = 30 * 60 * 1000

// A CGM session is over when the sensor stops reporting. Devices carry no end
// or expiry field, so silence is the only signal we have — and "a device row
// exists and was once activated" (the pre-2026-08-18 rule) made a sensor read
// Active forever after it came off the patient. Reported by Faithfull Friends
// via Dustin: pet Reuben's A0009XR read Active with its last reading 30 days
// old. 48 h is far outside any normal sync gap (the app uploads over BLE
// whenever the owner is near the pet) and well inside the documented 14-day
// wear life, so a sensor silent this long is off the patient or finished.
export const INACTIVE_SENSOR_MS = 48 * 60 * 60 * 1000

/**
 * CGM Status column value for one patient.
 *
 * Two sources of "when did this sensor last report", and the difference is the
 * whole point:
 *
 *  - `lastUpdatedAt` comes from the clinic-wide metrics fetch, which is a
 *    capped window (perPage 10000). Its ABSENCE is not evidence of a stopped
 *    sensor — fleet-wide that window spans only about five hours, so a pet can
 *    fall out of it while streaming perfectly well at a quieter cadence.
 *  - `authoritativeLastReading` comes from /devices-last-reading, which answers
 *    per device regardless of any display window. When it is supplied it is
 *    the truth, and silence past INACTIVE_SENSOR_MS means Inactive.
 *
 * Deriving status from the window alone is what made pet Reuben's A0009XR read
 * Active with a 31-day-old reading (field report 2026-08-19): the fix shipped
 * 2026-08-18 was correct in isolation but could never fire, because on an
 * admin-scoped dashboard the stale pets are exactly the ones missing from the
 * window.
 *
 * Degradation is automatic and deliberately lenient. While /devices-last-reading
 * is loading or failed the caller passes nothing, every device falls back to the
 * window, and the old behavior returns unchanged — a transient outage must not
 * mark the fleet Inactive, which would be this same bug with the sign flipped.
 * For the same reason a device that has never reported at all stays Active (24
 * pets on prod at the time of writing): its absence from the response is
 * indistinguishable from not-yet-fetched, and calling a just-activated sensor
 * Inactive is a worse error than the one being fixed.
 */
export const deriveCgmStatus = ({
  deviceCount,
  hasActivatedDevice,
  lastUpdatedAt,
  authoritativeLastReading,
  now = Date.now(),
}: {
  deviceCount: number
  hasActivatedDevice: boolean
  lastUpdatedAt?: Date
  authoritativeLastReading?: Date
  now?: number
}): CgmStatus => {
  if (deviceCount === 0) return 'No Device'
  // Take whichever source saw the sensor most recently. They should agree, but
  // max() means a lagging source can never drag a live sensor to Inactive.
  const seenAt =
    authoritativeLastReading && lastUpdatedAt
      ? new Date(Math.max(authoritativeLastReading.getTime(), lastUpdatedAt.getTime()))
      : (authoritativeLastReading ?? lastUpdatedAt)
  if (!seenAt && !hasActivatedDevice) return 'Pending'
  if (seenAt && now - seenAt.getTime() > INACTIVE_SENSOR_MS) return 'Inactive'
  return 'Active'
}

// Hyperglycemia warning thresholds (mmol/L) by species, mirroring
// go-services/internal/config/config.go SpeciesWarningHighThreshold so
// the dashboard badge fires under the same conditions the alert engine
// would fire under. Unknown species fall back to dog defaults, matching
// DefaultGlucoseThresholdsForSpecies. The pre-fix hardcoded > 13.9
// (a guess between dog warning and dog critical) was inconsistent with
// every species the engine knows about and especially wrong for cats,
// whose warning AND critical zone both start at 16.7.
//
// Deliberately NOT usePetTargetRange: that is the DISPLAY band (with the
// vet's chart override), while this badge predicts the ENGINE. Known
// residual gap: per-pet alert-rules overrides (which the engine honors)
// aren't visible here, so a pet with custom rules can badge on defaults.
export const hyperglycemiaThresholdForSpecies = (species?: string | null): number => {
  switch (species) {
    case 'cats':
      return 16.7
    case 'horses':
      return 6.6
    case 'rabbits':
      return 12.0
    default:
      return 11.1 // dogs / unknown
  }
}

export const getAlertStatus = ({
  correctedGlucose,
  hasActiveDevice,
  lastUpdatedAt,
  pendingConfirmation,
  followUpDate,
  species,
}: {
  correctedGlucose?: number
  hasActiveDevice: boolean
  lastUpdatedAt?: Date
  pendingConfirmation: boolean
  followUpDate?: string | null
  species?: string | null
}): AlertStatus => {
  if (pendingConfirmation) return 'Pending Client Confirmation'
  if (isToday(followUpDate)) return 'Follow-up Due'
  if (!hasActiveDevice) return 'No Active Device'
  if (!lastUpdatedAt) return 'Needs Review'

  const ageMs = Date.now() - lastUpdatedAt.getTime()
  if (ageMs > 6 * 60 * 60 * 1000) return 'Data Gap'

  const wouldBeHyper =
    typeof correctedGlucose === 'number' &&
    correctedGlucose > hyperglycemiaThresholdForSpecies(species)
  // NOTE: hypo boundary is species-INsensitive here while the hyper side is
  // species-aware — mirrors the Go alert engine's current behavior; revisit
  // if the engine gains per-species hypo thresholds.
  const wouldBeHypo =
    typeof correctedGlucose === 'number' && correctedGlucose < GLUCOSE_THRESHOLDS.LOW

  // Freshness gate: a stale reading in the alert zone is exactly what the
  // engine would NOT alert on (sustained-window check needs recent data),
  // so the dashboard must not promise an alert that the alerts subpage
  // can't back up.
  if (wouldBeHyper || wouldBeHypo) {
    if (ageMs > STALE_READING_MS) return 'Stale Reading'
    return wouldBeHypo ? 'Hypoglycemia Alert' : 'Hyperglycemia Alert'
  }

  return 'Stable'
}

// Exported for test: the 2026-08-18 CGM Status fix was correct inside
// deriveCgmStatus yet inert in production, because the defect lived in what
// this function FEEDS it. Testing the derivation alone cannot catch that class
// of bug again; the composition has to be exercised.
export const buildPatients = ({
  pets,
  devices,
  metrics,
  emrs,
  lastReadings,
}: {
  pets: Pet[]
  devices: Device[]
  metrics: Metric[]
  emrs: Emr[]
  lastReadings?: Map<number, Date> | null
}) => {
  const devicesByPet = new Map<number, Device[]>()
  for (const device of devices) {
    const petId = getPetId(device.pet)
    if (!petId) continue
    const list = devicesByPet.get(petId) || []
    list.push(device)
    devicesByPet.set(petId, list)
  }

  return pets.map<ClinicalPatient>((pet) => {
    const owner = getOwner((pet as Pet & { owner?: OwnerLike }).owner)
    const ownerName = owner?.name || 'Unknown owner'
    const ownerEmail = owner?.email || ''
    const clinicName = owner?.clinic_name || 'Clinic'
    const patientDevices = devicesByPet.get(pet.id) || []
    const latestMetric = getLatestMetricForDevices(metrics, patientDevices)
    const lastUpdatedAt = getMetricDate(latestMetric)
    const correctedGlucose =
      typeof latestMetric?.glucose === 'number'
        ? applyDLSBGMCorrection(latestMetric.glucose)
        : undefined
    const activeDevice = patientDevices[0]
    // Pending = accountStatus, NOT _verified: vet-registered owners are
    // created _verified:true with a claim email, and _verified is a hidden
    // auth field absent from depth-populated payloads anyway (ownerStatus.ts).
    const pendingConfirmation =
      owner?.accountStatus === 'invited' || owner?.accountStatus === 'pending_trial_verification'
    const followUpDate = getFollowUpDate(pet, emrs)
    // The clinic-wide metrics fetch is a capped window (perPage 10000 ≈ ~21
    // device-days across the whole clinic): a quiet pet's readings can fall
    // ENTIRELY outside it. An activated device with no in-window reading must
    // therefore read 'Active' (the alert column separately reports Needs
    // Review / Data Gap) — 'Pending' would misreport it as never-activated.
    const hasActivatedDevice = patientDevices.some(
      (d) => (typeof d.start_time === 'number' && d.start_time > 0) || d.activatedAt,
    )
    // Window-independent truth for this pet's sensors: the newest last-reading
    // across its devices. Absent while /devices-last-reading is loading or
    // failed, in which case deriveCgmStatus falls back to the capped window.
    const authoritativeLastReading = lastReadings
      ? patientDevices.reduce<Date | undefined>((newest, d) => {
          const seen = lastReadings.get(d.id)
          return seen && (!newest || seen > newest) ? seen : newest
        }, undefined)
      : undefined
    const cgmStatus = deriveCgmStatus({
      deviceCount: patientDevices.length,
      // A reading is itself proof the sensor was activated, even when the
      // device row carries neither start_time nor activatedAt.
      hasActivatedDevice: hasActivatedDevice || Boolean(latestMetric),
      lastUpdatedAt,
      authoritativeLastReading,
    })
    const alertStatus = getAlertStatus({
      correctedGlucose,
      hasActiveDevice: cgmStatus !== 'No Device',
      lastUpdatedAt,
      pendingConfirmation,
      followUpDate,
      species: pet.species,
    })
    const priority =
      alertStatus === 'Hypoglycemia Alert' ||
      alertStatus === 'Hyperglycemia Alert' ||
      alertStatus === 'Follow-up Due'
        ? 'High'
        : alertStatus === 'Needs Review' || alertStatus === 'Data Gap' || pendingConfirmation
          ? 'Medium'
          : 'Low'

    const searchText = [
      pet.name,
      pet.id,
      ownerName,
      ownerEmail,
      clinicName,
      activeDevice?.id,
      activeDevice?.name,
      pet.species,
      pet.breed,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return {
      ...pet,
      owner: (pet as Pet & { owner?: OwnerLike }).owner,
      ownerId: owner?.id,
      ownerName,
      ownerEmail,
      clinicName,
      deviceId: activeDevice?.id,
      deviceName: activeDevice?.name,
      cgmStatus,
      alertStatus,
      pendingConfirmation,
      followUpDate,
      lastReadingMmol: correctedGlucose,
      lastUpdatedAt,
      priority,
      searchText,
    }
  })
}

// "Recent Glucose Alerts" = a hypo/hyper alert updated within the last 24h.
// Shared by the KPI count and the list filter so the card and the filtered
// table always describe the exact same set of patients.
const isRecentAlert = (patient: ClinicalPatient) =>
  (patient.alertStatus === 'Hypoglycemia Alert' || patient.alertStatus === 'Hyperglycemia Alert') &&
  Boolean(patient.lastUpdatedAt) &&
  Date.now() - (patient.lastUpdatedAt as Date).getTime() <= 24 * 60 * 60 * 1000

const matchesFilter = (patient: ClinicalPatient, filter: FilterKey) => {
  switch (filter) {
    case 'active-cgm':
      return patient.cgmStatus === 'Active'
    case 'needs-review':
      return [
        'Needs Review',
        'Data Gap',
        'Sensor Issue',
        'Hypoglycemia Alert',
        'Hyperglycemia Alert',
      ].includes(patient.alertStatus)
    case 'recent-alerts':
      return isRecentAlert(patient)
    case 'hypoglycemia':
      return patient.alertStatus === 'Hypoglycemia Alert'
    case 'hyperglycemia':
      return patient.alertStatus === 'Hyperglycemia Alert'
    case 'pending-confirmation':
      return patient.pendingConfirmation
    case 'follow-up':
      return patient.alertStatus === 'Follow-up Due' || Boolean(patient.followUpDate)
    case 'my-patients':
    case 'all':
      return true
  }
}

const StatusBadge = ({ status }: { status: AlertStatus }) => {
  const translate = useTranslate()
  const colors = STATUS_TONES[statusTone[status]]
  return (
    <Chip
      label={translate(`pos.dashboard.clinical.status.${status}`)}
      size="small"
      sx={{
        height: 22,
        borderRadius: '999px',
        border: '1px solid',
        borderColor: colors.borderColor,
        bgcolor: colors.bgcolor,
        color: colors.color,
        fontSize: 12,
        fontWeight: 600,
      }}
    />
  )
}

const PriorityDot = ({ priority }: { priority: ClinicalPatient['priority'] }) => {
  const color =
    priority === 'High'
      ? TONE_ACCENT.critical
      : priority === 'Medium'
        ? TONE_ACCENT.attention
        : TONE_ACCENT.positive
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: color,
      }}
    />
  )
}

const ActiveDot = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: '#22c55e',
      animation: 'vetpoPulse 1.6s infinite',
      '@keyframes vetpoPulse': {
        '0%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.45)' },
        '70%': { boxShadow: '0 0 0 6px rgba(34, 197, 94, 0)' },
        '100%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0)' },
      },
    }}
  />
)

/**
 * KPI tone. The four cards used to be hand-picked blue / orange / blue / red
 * at all times, so an empty alert queue still showed a big red "0" — the
 * loudest thing on the page, and it meant nothing (UI-STYLE-GUIDE §1: loud
 * color is for real clinical alerts only). Counts that carry no clinical
 * priority are neutral; the two that do take their tone only when non-zero.
 */
export const kpiTone = (
  kind: 'active-cgm' | 'needs-review' | 'pending-confirmation' | 'recent-alerts',
  value: number,
): StatusTone => {
  if (value <= 0) return 'neutral'
  if (kind === 'recent-alerts') return 'critical'
  if (kind === 'needs-review') return 'attention'
  return 'neutral'
}

const ClinicalStatCard = ({
  icon,
  value,
  title,
  subtitle,
  tone,
  active,
  onClick,
  testId,
}: {
  icon: React.ReactNode
  value: number
  title: string
  subtitle: string
  tone: StatusTone
  active: boolean
  onClick: () => void
  testId?: string
}) => (
  <Card
    onClick={onClick}
    data-testid={testId}
    data-tone={tone}
    sx={{
      cursor: 'pointer',
      height: '100%',
      borderRadius: 2,
      border: '1px solid',
      borderColor: active ? 'primary.main' : '#e5e7eb',
      boxShadow: active ? '0 0 0 3px rgba(79, 60, 201, 0.14)' : '0 1px 2px rgba(16, 24, 40, 0.05)',
      transition: 'border-color 160ms ease, box-shadow 160ms ease',
      '&:hover': {
        borderColor: active ? 'primary.main' : '#d1d5db',
        boxShadow: active
          ? '0 0 0 3px rgba(79, 60, 201, 0.14)'
          : '0 4px 12px rgba(16, 24, 40, 0.08)',
      },
    }}
  >
    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: tone === 'neutral' ? 'text.secondary' : STATUS_TONES[tone].color,
            bgcolor: STATUS_TONES[tone].bgcolor,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ color: '#111827', fontWeight: 600, lineHeight: 1.3 }}>
          {title}
        </Typography>
      </Box>
      <Typography
        variant="h4"
        sx={{
          color: tone === 'critical' ? STATUS_TONES.critical.color : '#111827',
          fontWeight: 700,
          lineHeight: 1,
          mb: 0.75,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {/* Hidden on phones: in a 2-up grid it wrapped to three lines, and the
          title + number already say what the card is. */}
      <Typography
        variant="caption"
        component="p"
        sx={{ color: 'text.secondary', lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}
      >
        {subtitle}
      </Typography>
    </CardContent>
  </Card>
)

const buildActivityItems = ({
  patients,
  activities,
  emrs,
  translate,
}: {
  patients: ClinicalPatient[]
  activities: Activity[]
  emrs: Emr[]
  translate: (key: string, options?: Record<string, unknown>) => string
}): ActivityItem[] => {
  const patientById = new Map(patients.map((patient) => [patient.id, patient]))
  const generated: ActivityItem[] = []

  for (const patient of patients) {
    if (patient.lastUpdatedAt) {
      generated.push({
        id: `reading-${patient.id}`,
        color: '#60a5fa',
        description: translate('pos.dashboard.clinical.activity.new_reading'),
        patientName:
          patient.name || translate('pos.dashboard.clinical.pet_fallback', { id: patient.id }),
        timeLabel: relativeTime(patient.lastUpdatedAt, translate),
        petId: patient.id,
      })
    }
    if (
      patient.alertStatus === 'Hypoglycemia Alert' ||
      patient.alertStatus === 'Hyperglycemia Alert'
    ) {
      generated.push({
        id: `alert-${patient.id}`,
        color: '#ff6470',
        description: translate(
          patient.alertStatus === 'Hypoglycemia Alert'
            ? 'pos.dashboard.clinical.activity.hypoglycemia_detected'
            : 'pos.dashboard.clinical.activity.hyperglycemia_detected',
        ),
        patientName:
          patient.name || translate('pos.dashboard.clinical.pet_fallback', { id: patient.id }),
        timeLabel: relativeTime(patient.lastUpdatedAt, translate),
        petId: patient.id,
      })
    }
    if (patient.pendingConfirmation) {
      generated.push({
        id: `pending-${patient.id}`,
        color: '#9ca3af',
        description: translate('pos.dashboard.clinical.activity.pending_confirmation'),
        patientName:
          patient.name || translate('pos.dashboard.clinical.pet_fallback', { id: patient.id }),
        timeLabel: patient.createdAt
          ? relativeTime(patient.createdAt, translate)
          : translate('pos.dashboard.clinical.time.recently'),
        petId: patient.id,
      })
    }
  }

  for (const activity of activities.slice(0, 8)) {
    const petId = getPetId(activity.pet)
    const patient = petId ? patientById.get(petId) : undefined
    const activityTime =
      activity.createdAt ||
      activity.created_at ||
      (activity.start_time ? activity.start_time * 1000 : null)
    generated.push({
      id: `activity-${activity.id}`,
      color: '#22c55e',
      description: activity.description
        ? formatActivityDescription(
            { type: activity.type, description: activity.description },
            translate,
          )
        : translate('pos.dashboard.clinical.activity.client_added', {
            type: activity.type || 'log',
          }),
      patientName:
        patient?.name ||
        (typeof activity.pet === 'object'
          ? activity.pet.name || translate('pos.dashboard.clinical.species_fallback')
          : translate('pos.dashboard.clinical.species_fallback')),
      timeLabel: relativeTime(activityTime, translate),
      // Only link when the pet is in the vet's accessible patient set. An
      // activity can reference a pet that was deleted or whose share was
      // revoked; linking to it navigated to /pets/<id>/show → 404 → the
      // "Element does not exist" toast (and a dump to the Clients list). Without
      // a resolvable patient, render the row as non-clickable instead.
      // (getPetId can yield null; `?? undefined` keeps the field number|undefined.)
      petId: patient ? (petId ?? undefined) : undefined,
    })
  }

  for (const emr of emrs.slice(0, 8)) {
    const patient = patientById.get(emr.pet)
    generated.push({
      id: `note-${emr.id}`,
      color: '#a855f7',
      description: translate('pos.dashboard.clinical.activity.vet_note'),
      patientName:
        patient?.name || translate('pos.dashboard.clinical.pet_fallback', { id: emr.pet }),
      timeLabel: relativeTime(emr.createdAt || emr.timestamp, translate),
      // Same guard as activities: only link to a pet we can actually open.
      petId: patient ? emr.pet : undefined,
    })
  }

  return generated
    .filter((item, index, list) => list.findIndex((other) => other.id === item.id) === index)
    .slice(0, 6)
}

/** "Jane M Doe" → "JD"; single word → first two letters; falls back to "?" */
const getInitials = (name?: string | null): string => {
  if (!name) return '?'
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

const ClinicalDashboard = () => {
  const redirect = useRedirect()
  const notify = useNotify()
  const translate = useTranslate()
  const locale = useLocale()
  const { identity } = useGetIdentity()
  const vetName = identity?.fullName || ''
  // vic/vetpo#13: restore a patient hidden in an earlier session. Same gate
  // as HidePetButton — GET /api/pets/clinic-hidden 403s any role but 'vet'.
  const canSeeHidden = identity?.role === 'vet'
  const [hiddenOpen, setHiddenOpen] = useState(false)
  // Caption line: clinic when set, otherwise the vet's email
  const vetCaption =
    (identity as { clinicName?: string | null } | undefined)?.clinicName ||
    (identity as { email?: string } | undefined)?.email ||
    ''
  const [, setSelectedPetId] = useStore<any>('selectedPetId')
  // selectedDeviceId is app-global: navigating to a patient WITHOUT clearing
  // it lets the previous patient's device id leak into the new pet's charts
  // via the `propDeviceId ?? storeDeviceId` fallback (cross-pet leak class —
  // same reason PetDatagrid clears it on row click).
  const [, setSelectedDeviceId] = useStore<any>('selectedDeviceId')
  // Active display unit (mmol/L | mg/dL) — the same store key the app-bar
  // switcher and every chart subscribe to, so the dashboard's glucose
  // readings flip in lockstep instead of staying pinned to mmol/L.
  const [glucoseUnit] = useStore<string>('glucoseUnit', 'mmol/L')
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all')
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)

  // Click a header: toggle direction if it's already the sort key, otherwise
  // switch to that column at its sensible default direction.
  const handleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: DEFAULT_ORDER[field] },
    )
  }
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [resendingOwnerId, setResendingOwnerId] = useState<number | null>(null)
  const patientListRef = useRef<HTMLDivElement>(null)

  // Re-send the claim/activation email to a pending owner (P0: Resend must
  // actually send mail, not navigate). Same endpoint + handling as OwnersList.
  const handleResend = async (patient: ClinicalPatient) => {
    if (!patient.ownerId) {
      notify('pos.owners.resend_error', { type: 'error' })
      return
    }
    setResendingOwnerId(patient.ownerId)
    try {
      const res = await deadlineFetch('/api/users/resend-owner-invite', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ownerId: patient.ownerId }),
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        notify(
          data?.alreadyActive ? 'pos.owners.resend_already_active' : 'pos.owners.resend_success',
          { type: data?.alreadyActive ? 'info' : 'success' },
        )
      } else if (res.status === 429) {
        notify('pos.owners.resend_rate_limited', { type: 'warning' })
      } else {
        notify('pos.owners.resend_error', { type: 'error' })
      }
    } catch {
      notify('pos.owners.resend_error', { type: 'error' })
    } finally {
      setResendingOwnerId(null)
    }
  }

  const {
    data: pets = [],
    total: petsTotal,
    isLoading: petsLoading,
    error: petsError,
  } = useGetList<Pet>('pets', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'createdAt', order: 'DESC' },
    filter: {},
    meta: { params: { depth: 2 } },
  })
  const {
    data: devices = [],
    total: devicesTotal,
    isLoading: devicesLoading,
    error: devicesError,
  } = useGetList<Device>('devices', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'start_time', order: 'DESC' },
    filter: {},
    meta: { params: { depth: 1 } },
  })
  const {
    data: metrics = [],
    isLoading: metricsLoading,
    error: metricsError,
  } = useGetList<Metric>('metrics', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'timestamp', order: 'DESC' },
    filter: {},
    meta: { params: { depth: 0 } },
  })
  const { data: activities = [], error: activitiesError } = useGetList<Activity>('activities', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'createdAt', order: 'DESC' },
    filter: {},
    meta: { params: { depth: 1 } },
  })
  const { data: emrs = [], error: emrsError } = useGetList<Emr>('emrs', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'createdAt', order: 'DESC' },
    filter: {},
    meta: { params: { depth: 1 } },
  })

  // Window-independent last-reading times. Null while loading or on failure,
  // which makes buildPatients fall back to the capped metrics window.
  const { lastReadings } = useDeviceLastReadings()

  const patients = useMemo(
    () => buildPatients({ pets, devices, metrics, emrs, lastReadings }),
    [pets, devices, metrics, emrs, lastReadings],
  )

  const stats = useMemo(
    () => ({
      activeCgm: patients.filter((patient) => patient.cgmStatus === 'Active').length,
      needsReview: patients.filter((patient) =>
        [
          'Needs Review',
          'Data Gap',
          'Sensor Issue',
          'Hypoglycemia Alert',
          'Hyperglycemia Alert',
        ].includes(patient.alertStatus),
      ).length,
      pendingConfirmation: patients.filter((patient) => patient.pendingConfirmation).length,
      recentAlerts: patients.filter(isRecentAlert).length,
      followUpToday: patients.filter((patient) => isToday(patient.followUpDate)).length,
    }),
    [patients],
  )

  const pendingPatients = patients.filter((patient) => patient.pendingConfirmation)
  const activityItems = buildActivityItems({ patients, activities, emrs, translate })

  const filteredPatients = useMemo(() => {
    const matched = patients.filter((patient) => {
      const matchesSearch = search.trim()
        ? patient.searchText.includes(search.trim().toLowerCase())
        : true
      return matchesSearch && matchesFilter(patient, activeFilter)
    })
    return matched.sort((a, b) => comparePatients(a, b, sort))
  }, [patients, search, activeFilter, sort])

  const applyFilter = (filter: FilterKey) => {
    setActiveFilter(filter)
    window.requestAnimationFrame(() => {
      patientListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  // Phones/tablets get a card list instead of an 8-column table that
  // scrolled sideways with ~2 columns visible (desktop path unchanged).
  const isCompact = useIsCompact()

  const openPatient = (patient: ClinicalPatient) => {
    setSelectedDeviceId(undefined)
    setSelectedPetId(patient.id)
    redirect('show', 'pets', patient.id)
  }

  const loading = petsLoading || devicesLoading || metricsLoading

  // A list that FAILED (after the app-wide retries) arrives as []: every
  // patient then reads "No Device" (devices), no glucose so no Hypoglycemia /
  // Hyperglycemia status can appear (metrics — the heavy-list 429 bucket), or
  // the recent-activity rows are missing. Without this the page looked like an
  // all-quiet clinic. Statuses are still built from what did load; this says
  // they are incomplete (eng-brain pass 77).
  const loadFailed = !!(petsError || devicesError || metricsError || activitiesError || emrsError)

  // Every list above asks for perPage 10000, and ada-core CAPS the read:
  // devices at DEVICES_MAX_LIMIT (1,000 for a normal role, 25,000 for an
  // extended-admin one), metrics at METRICS_MAX_LIMIT. The server logs
  // "response is TRUNCATED" and returns 200 — so the truncation was visible in
  // ada-core's logs and nowhere a vet could see it. Measured live 2026-08-19:
  // 1,000 of 1,592 devices on a fleet-wide account, and because the sort is
  // `start_time DESC` the OLDEST sensors are the ones dropped. A patient whose
  // devices all fall past the cap renders 'No Device' — indistinguishable from
  // one that never had a sensor — and never gets an authoritative last-reading
  // lookup either, so the 36dfafa5 CGM Status fix cannot reach it.
  //
  // `total` absent is UNKNOWN, never truncated: a banner that fires on an
  // ordinary response is a banner vets learn to ignore.
  //
  // METRICS IS DELIBERATELY EXCLUDED. Its fetch carries `filter: {}` and is a
  // rolling window by design — `loaded < total` is its permanent, correct state
  // for any clinic with history, so warning on it would fire on every account
  // forever and train vets to dismiss the one notice that matters. That the
  // window cannot be complete is precisely WHY deriveCgmStatus takes an
  // authoritative last-reading source. Pets and devices are different: both are
  // meant to arrive whole, so short delivery there is a real anomaly.
  const truncations = useMemo<TruncatableList[]>(
    () => [
      { resource: 'pets', loaded: pets.length, total: petsTotal },
      { resource: 'devices', loaded: devices.length, total: devicesTotal },
    ],
    [pets.length, petsTotal, devices.length, devicesTotal],
  )

  return (
    <Box>
      {!loading && loadFailed && (
        <Alert severity="warning" data-testid="dashboard-load-error-notice" sx={{ mb: 2 }}>
          <AlertTitle>{translate('pos.dashboard.clinical.load_error.title')}</AlertTitle>
          {translate('pos.dashboard.clinical.load_error.detail')}
        </Alert>
      )}
      {!loading && <DataTruncationNotice lists={truncations} />}
      <UnassignedPatientBanner />
      <PageHeader
        title={translate('pos.dashboard.clinical.title')}
        subtitle={formatDate(new Date(), locale)}
        actions={
          vetName ? (
            // Who is signed in. Hidden on phones, where it used to leave the
            // initials bubble stranded on its own line under the title.
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" sx={{ color: '#111827', fontWeight: 600 }}>
                  {vetName}
                </Typography>
                {vetCaption && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {vetCaption}
                  </Typography>
                )}
              </Box>
              <Box
                aria-hidden
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: STATUS_TONES.neutral.bgcolor,
                  color: STATUS_TONES.neutral.color,
                  border: '1px solid',
                  borderColor: STATUS_TONES.neutral.borderColor,
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                }}
              >
                {getInitials(vetName)}
              </Box>
            </Stack>
          ) : undefined
        }
      />

      <Box
        sx={{
          display: 'grid',
          // Four KPIs (Follow-up Due is hidden, below) — the grid used to keep
          // a fifth, empty lg column, so the cards stopped 280px short of the
          // tables under them. 2x2 on phones instead of four screen-tall cards.
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: { xs: 1.5, sm: 2 },
          mb: 3,
        }}
      >
        <ClinicalStatCard
          icon={<BoltIcon fontSize="small" />}
          value={stats.activeCgm}
          title={translate('pos.dashboard.clinical.stat.active_cgm_title')}
          subtitle={translate('pos.dashboard.clinical.stat.active_cgm_subtitle')}
          tone={kpiTone('active-cgm', stats.activeCgm)}
          testId="kpi-active-cgm"
          active={activeFilter === 'active-cgm'}
          onClick={() => applyFilter('active-cgm')}
        />
        <ClinicalStatCard
          icon={<ReportProblemOutlinedIcon fontSize="small" />}
          value={stats.needsReview}
          title={translate('pos.dashboard.clinical.stat.needs_review_title')}
          subtitle={translate('pos.dashboard.clinical.stat.needs_review_subtitle')}
          tone={kpiTone('needs-review', stats.needsReview)}
          testId="kpi-needs-review"
          active={activeFilter === 'needs-review'}
          onClick={() => applyFilter('needs-review')}
        />
        <ClinicalStatCard
          icon={<AccessTimeIcon fontSize="small" />}
          value={stats.pendingConfirmation}
          title={translate('pos.dashboard.clinical.stat.pending_confirmation_title')}
          subtitle={translate('pos.dashboard.clinical.stat.pending_confirmation_subtitle')}
          tone={kpiTone('pending-confirmation', stats.pendingConfirmation)}
          testId="kpi-pending-confirmation"
          active={activeFilter === 'pending-confirmation'}
          onClick={() => applyFilter('pending-confirmation')}
        />
        <ClinicalStatCard
          icon={<NotificationsNoneIcon fontSize="small" />}
          value={stats.recentAlerts}
          title={translate('pos.dashboard.clinical.stat.recent_alerts_title')}
          subtitle={translate('pos.dashboard.clinical.stat.recent_alerts_subtitle')}
          tone={kpiTone('recent-alerts', stats.recentAlerts)}
          testId="kpi-recent-alerts"
          active={activeFilter === 'recent-alerts'}
          onClick={() => applyFilter('recent-alerts')}
        />
        {/* "Follow-up Due Today" KPI hidden until vet follow-up scheduling is
            built — followUpDate is never set, so this card was always 0. */}
      </Box>

      <Box
        sx={{
          display: 'grid',
          // The patient table needs ~970px. At 2fr/1fr it got 875px on a
          // 1440 laptop (811 at 1280) and scrolled sideways inside its card,
          // cutting every row's View button in half with no visible cue. So
          // the activity rail sits beside it only where both fit, and stacks
          // below it on narrower screens.
          gridTemplateColumns: '1fr',
          '@media (min-width: 1440px)': { gridTemplateColumns: 'minmax(0, 1fr) 300px' },
          gap: 3,
        }}
      >
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Card
            ref={patientListRef}
            sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)' }}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ px: 2.5, py: 2 }}
              >
                <Typography variant="h6" sx={{ color: '#111827' }}>
                  {translate('pos.dashboard.clinical.active_cgm_patients')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder={translate('pos.dashboard.clinical.search_placeholder')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearch('')}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    sx={{ width: { xs: '100%', sm: 360 } }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFiltersOpen((open) => !open)}
                    sx={{
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      borderColor: activeFilter === 'all' ? 'divider' : 'primary.main',
                    }}
                  >
                    {translate('pos.dashboard.clinical.filter')}
                  </Button>
                  {canSeeHidden && (
                    <Button
                      color="inherit"
                      onClick={() => setHiddenOpen(true)}
                      data-testid="open-hidden-patients"
                      sx={{ color: 'text.secondary' }}
                    >
                      {translate('pos.dashboard.clinical.hidden_patients')}
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Collapse in={filtersOpen}>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ px: 2.5, pb: 2 }}
                >
                  {filterOptions.map((option) => (
                    <Chip
                      key={option.key}
                      label={translate(`pos.dashboard.clinical.filters.${option.key}`)}
                      color={activeFilter === option.key ? 'primary' : 'default'}
                      variant={activeFilter === option.key ? 'filled' : 'outlined'}
                      onClick={() => setActiveFilter(option.key)}
                    />
                  ))}
                </Stack>
              </Collapse>

              {isCompact ? (
                <Stack
                  data-testid="clinical-patient-cards"
                  sx={{ borderTop: '1px solid #eef2f7' }}
                  divider={<Box sx={{ borderTop: '1px solid #eef2f7' }} />}
                >
                  {loading ? (
                    <Typography sx={{ py: 4, color: '#64748b', textAlign: 'center' }}>
                      {translate('pos.dashboard.clinical.loading')}
                    </Typography>
                  ) : filteredPatients.length === 0 ? (
                    <Typography sx={{ py: 4, color: '#64748b', textAlign: 'center' }}>
                      {translate('pos.dashboard.clinical.no_match')}
                    </Typography>
                  ) : (
                    filteredPatients.map((patient) => {
                      const body = (
                        <Box sx={{ px: 2, py: 1.5 }}>
                          <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                            <Box sx={{ pt: 0.75 }}>
                              <PriorityDot priority={patient.priority} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}
                              >
                                {patient.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {patient.ownerName} · {patient.clinicName}
                              </Typography>
                            </Box>
                            <StatusBadge status={patient.alertStatus} />
                          </Stack>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ mt: 1, pl: 2.5, color: '#64748b' }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#111827',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {typeof patient.lastReadingMmol === 'number'
                                ? formatGlucoseForDisplay(patient.lastReadingMmol, glucoseUnit)
                                : translate('pos.dashboard.clinical.no_data')}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {patient.cgmStatus === 'Active' && <ActiveDot />}
                              <Typography variant="caption">
                                {translate(`pos.dashboard.clinical.cgm.${patient.cgmStatus}`)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ ml: 'auto !important' }}>
                              {relativeTime(patient.lastUpdatedAt, translate)}
                            </Typography>
                          </Stack>
                        </Box>
                      )
                      // The card button deliberately has NO aria-label: a label would
                      // REPLACE its content as the accessible name, hiding the alert
                      // badge, glucose and CGM status from a screen reader (P75-5).
                      // Pending clients have nothing to open yet — their action
                      // is Resend, exactly as in the table.
                      return patient.pendingConfirmation ? (
                        <Box key={patient.id}>
                          {body}
                          <Box sx={{ px: 2, pb: 1.5, pl: 4.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={resendingOwnerId === patient.ownerId}
                              onClick={() => handleResend(patient)}
                            >
                              {translate('pos.dashboard.clinical.resend')}
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <CardActionArea key={patient.id} onClick={() => openPatient(patient)}>
                          {body}
                        </CardActionArea>
                      )
                    })
                  )}
                </Stack>
              ) : (
                <TableContainer sx={{ borderTop: '1px solid #eef2f7' }}>
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableCell-root': { px: 1.5 },
                      '& .MuiTableCell-root:first-of-type': { pl: 2.5 },
                    }}
                  >
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        {(
                          [
                            'priority',
                            'patient',
                            'species_breed',
                            'cgm_status',
                            'last_reading',
                            'alert_status',
                            'last_updated',
                            'action',
                          ] as const
                        ).map((header) => {
                          const sortable = header !== 'action'
                          const field = header as SortField
                          const active = sortable && sort.field === field
                          const label = translate(`pos.dashboard.clinical.table.${header}`)
                          return (
                            <TableCell
                              key={header}
                              sortDirection={active ? sort.order : false}
                              sx={{ py: 1.5, whiteSpace: 'nowrap' }}
                            >
                              {sortable ? (
                                <TableSortLabel
                                  active={active}
                                  direction={active ? sort.order : DEFAULT_ORDER[field]}
                                  onClick={() => handleSort(field)}
                                  sx={{
                                    color: 'inherit',
                                    '&.Mui-active': { color: '#111827' },
                                    '&.Mui-active .MuiTableSortLabel-icon': { color: '#111827' },
                                  }}
                                >
                                  {label}
                                </TableSortLabel>
                              ) : (
                                label
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ py: 4, color: '#64748b', textAlign: 'center' }}
                          >
                            {translate('pos.dashboard.clinical.loading')}
                          </TableCell>
                        </TableRow>
                      ) : filteredPatients.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ py: 4, color: '#64748b', textAlign: 'center' }}
                          >
                            {translate('pos.dashboard.clinical.no_match')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredPatients.map((patient) => (
                          <TableRow
                            key={patient.id}
                            hover
                            sx={{
                              '&:last-child td': { borderBottom: 0 },
                              '&:hover': { bgcolor: '#f8fafc' },
                            }}
                          >
                            <TableCell align="center">
                              <PriorityDot priority={patient.priority} />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: '#111827' }}
                              >
                                {patient.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {patient.ownerName} · {patient.clinicName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#334155' }}>
                                {patient.species ||
                                  translate('pos.dashboard.clinical.species_fallback')}{' '}
                                ·{' '}
                                {patient.breed ||
                                  translate('pos.dashboard.clinical.breed_fallback')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                {patient.cgmStatus === 'Active' && <ActiveDot />}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: patient.cgmStatus === 'Active' ? '#16a34a' : '#64748b',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {translate(`pos.dashboard.clinical.cgm.${patient.cgmStatus}`)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: '#111827',
                                  whiteSpace: 'nowrap',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {typeof patient.lastReadingMmol === 'number'
                                  ? formatGlucoseForDisplay(patient.lastReadingMmol, glucoseUnit)
                                  : translate('pos.dashboard.clinical.no_data')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={patient.alertStatus} />
                            </TableCell>
                            <TableCell sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                              {relativeTime(patient.lastUpdatedAt, translate)}
                            </TableCell>
                            <TableCell>
                              {patient.pendingConfirmation ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={resendingOwnerId === patient.ownerId}
                                  onClick={() => handleResend(patient)}
                                >
                                  {translate('pos.dashboard.clinical.resend')}
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  onClick={() => openPatient(patient)}
                                  sx={{
                                    color: 'text.secondary',
                                    borderColor: 'divider',
                                    '&:hover': {
                                      borderColor: 'text.secondary',
                                      bgcolor: 'action.hover',
                                    },
                                  }}
                                >
                                  {translate('pos.dashboard.clinical.view')}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {hiddenOpen && (
            <Dialog open onClose={() => setHiddenOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle>{translate('pos.dashboard.clinical.hidden_patients')}</DialogTitle>
              <DialogContent sx={{ px: { xs: 0, sm: 3 } }}>
                <HiddenPetsPanel />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHiddenOpen(false)}>{translate('ra.action.close')}</Button>
              </DialogActions>
            </Dialog>
          )}

          {pendingPatients.length > 0 && (
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2.5, py: 2 }}>
                  <AccessTimeIcon sx={{ color: '#2563eb' }} />
                  <Typography variant="h6" sx={{ color: '#111827' }}>
                    {translate('pos.dashboard.clinical.pending_setup')}
                  </Typography>
                  <Chip label={pendingPatients.length} color="primary" size="small" />
                </Stack>
                <TableContainer sx={{ borderTop: '1px solid #eef2f7' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        {['pet', 'client', 'email', 'status', 'created', 'action'].map((header) => (
                          <TableCell key={header} sx={{ whiteSpace: 'nowrap' }}>
                            {translate(`pos.dashboard.clinical.table.${header}`)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingPatients.map((patient) => (
                        <TableRow key={patient.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{patient.name}</TableCell>
                          <TableCell>{patient.ownerName}</TableCell>
                          <TableCell>
                            {patient.ownerEmail || translate('pos.dashboard.clinical.no_email')}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status="Pending Client Confirmation" />
                          </TableCell>
                          <TableCell>
                            {patient.createdAt
                              ? new Date(patient.createdAt).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={resendingOwnerId === patient.ownerId}
                                onClick={() => handleResend(patient)}
                              >
                                {translate('pos.dashboard.clinical.resend_email')}
                              </Button>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => redirect('edit', 'pets', patient.id)}
                              >
                                {translate('pos.dashboard.clinical.edit')}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Stack>

        <Card
          sx={{
            borderRadius: 2,
            alignSelf: 'start',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
          }}
        >
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Typography variant="h6" sx={{ px: 2.5, py: 2, color: '#111827' }}>
              {translate('pos.dashboard.clinical.recent_activity')}
            </Typography>
            <Box sx={{ borderTop: '1px solid #eef2f7' }}>
              {activityItems.length === 0 ? (
                <Typography sx={{ px: 2.5, py: 3, color: '#64748b' }}>
                  {translate('pos.dashboard.clinical.no_recent_activity')}
                </Typography>
              ) : (
                activityItems.map((item) => (
                  <Stack
                    key={item.id}
                    direction="row"
                    spacing={1.5}
                    sx={{
                      px: 2.5,
                      py: 2,
                      borderBottom: '1px solid #eef2f7',
                      cursor: item.petId ? 'pointer' : 'default',
                      '&:hover': item.petId ? { bgcolor: '#f8fafc' } : undefined,
                    }}
                    onClick={() => {
                      if (item.petId) {
                        setSelectedDeviceId(undefined)
                        setSelectedPetId(item.petId)
                        redirect('show', 'pets', item.petId)
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        mt: 0.75,
                        flex: '0 0 auto',
                      }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ color: '#0f172a' }}>
                        {item.description}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>
                          {item.patientName}
                        </Box>{' '}
                        · {item.timeLabel}
                      </Typography>
                    </Box>
                  </Stack>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default ClinicalDashboard
